import { canShowJybCoinFeatures } from '@g/utils/projectUtils'
import {
  airVolumeG1,
  getSeShpMethod91VolumeLimitCm,
  volumeLimitSe,
} from '@g/utils/parcelUtils'
import {
  isOrderCreateDamageInsuranceEnabled,
  totalSelectedDamageInsurancePremium,
} from '@g/utils/damageInsurance'
import { buildFeeBaseSnapshot } from '@g/order-create/fee/buildFeeBaseSnapshot'
import { buildFeeSummary as buildFinalFeeSummary } from '@g/order-create/fee/buildFeeSummary'
import { buildFeeRows } from '@g/order-create/fee/buildFeeRows'
import { computeFeeInfo } from '@g/order-create/fee/feeInfo'
import {
  computeFinalSrParcelsFeeFormulas,
  computeParcelsFeeFormulas,
} from '@g/order-create/fee/feeFormulas'
import {
  computeIsBusying,
  computeJyoBusyFeeData,
  computeJyoBusyFeeWg,
  computeLocalFee,
} from '@g/order-create/fee/busyFee'
import {
  computeChargeHctFeeCai,
  computeHctPalletFee,
  computeHctPalletFeeData,
  computeJyHctCai,
  computeJyHctCaiFee,
  computeJyHctCaiFeeData,
  computeOneCaiFee,
  computeOneHctPalletFee,
  computePreHctPalletFee,
  computePreJyoOvldFee,
} from '@g/order-create/fee/hctFee'

function platformPolicy (project) {
  const isH5 = String(project).endsWith('h5')
  return {
    apartFeePerParcel: isH5 ? 100 : 0,
    couponBeforeTimeAndTax: !isH5,
    emptyAddressPalletFee: isH5 ? 1000 : 0,
    limitKtjStrategy: isH5 ? 'h5-parcel-utils' : 'pc-legacy',
    useEstimateWeight: isH5,
  }
}

function resolveIsBizFee ({ state, getters, rootState }) {
  if (getters.isCompanyAddr) return true
  if (
    rootState.edition === 's' ||
    state.createForm.useFeeType === 'busi' ||
    state.createForm.useFeeType === 'notIncTax'
  ) {
    return state.createPrepare.parcelsInfo.jyParcelBiz > 0
  }
  return false
}

export function resolveFinalUseFirstCarrier ({ state, getters, policy }) {
  if (state.createForm.jyoCarrier) return state.createForm.jyoCarrier
  return resolveFactualFirstCarrier({ state, getters, policy })
}

export function resolveFactualFirstCarrier ({ state, getters, policy }) {
  const shippingRate = getters.shippingRatesInfo
  const firstCarrier = getters.getFirstCarrierBy({
    addrInfo: state.currAddrInfo,
    csType: state.createView.deliveryMode,
    isBusiness: state.createForm.jyoShpMethod !== '1',
    estimateWeight: policy.useEstimateWeight
      ? state.createView.estimateWeight
      : undefined,
  })
  if (shippingRate.transport === 'sea') return ''
  if (shippingRate.transport === 'se' && firstCarrier === 'ktj') return 'hct'
  if (firstCarrier === 'hct' && getters.limitHct) return 'hct'
  if (firstCarrier === 'ktj' && getters.limitKtjBy({
    strategy: policy.limitKtjStrategy,
    transport: shippingRate.transport,
  })) return 'hct'
  return firstCarrier
}

export function resolvePreHctPalletFee ({ state, getters, rootState, policy }) {
  const palletCount = getters.hasHctHatPalletFeeParcels.length
  const hctPalletFeeData = computeHctPalletFeeData({
    showJybCoinFeatures: canShowJybCoinFeatures(rootState.project),
    userKnapsackHctPalletFee: state.createPrepare?.userKnapsackHctPalletFee,
    palletCount,
  })
  const oneHctPalletFee = state.currAddrInfo
    ? computeOneHctPalletFee({
      addrInfo: state.currAddrInfo,
      hctCitiesPalletFee: state.createPrepare?.hctCitiesPalletFee,
    })
    : policy.emptyAddressPalletFee
  return computePreHctPalletFee({
    oneHctPalletFee,
    palletCount,
    useKnsCount: hctPalletFeeData.useKnsCount,
  })
}

function resolveJyoOvldFee ({ state, getters }) {
  const prepare = state.createPrepare
  const shippingRate = getters.shippingRatesInfo
  const weightLight = prepare.parcelsInfo.jyWeight > 0 &&
    prepare.parcels.some(parcel => parcel.weight >= 69500)
  let onlyShpMethod91ByVolumeLimit = false
  if (shippingRate.transport === 'se') {
    const provider = prepare.lcmrouteSe?.twcc?.provider
    onlyShpMethod91ByVolumeLimit = prepare.parcels.some(parcel => {
      const maxWidth = getSeShpMethod91VolumeLimitCm({
        jyAddrid: parcel.jyAddrid || prepare.parcelsInfo?.jyAddrid,
        provider,
      })
      return volumeLimitSe(parcel, maxWidth)
    })
  } else if (shippingRate.transport === 'air') {
    onlyShpMethod91ByVolumeLimit = prepare.parcels.some(parcel => airVolumeG1(parcel))
  }
  const fee = computePreJyoOvldFee({ weightLight, onlyShpMethod91ByVolumeLimit })
  return shippingRate.transport === 'se' && fee > 0 ? fee : 0
}

function resolveHctFees ({ state, getters, rootState, finalUseFirstCarrier, policy }) {
  const prepare = state.createPrepare
  const showJybCoinFeatures = canShowJybCoinFeatures(rootState.project)
  const chargeHctFeeCai = computeChargeHctFeeCai({
    jyAddrid: prepare.parcelsInfo.jyAddrid,
  })
  const jyHctCai = computeJyHctCai({
    hasHctCaiFeeParcels: getters.hasHctCaiFeeParcels,
    chargeHctFeeCai,
  })
  const jyHctCaiFeeData = computeJyHctCaiFeeData({
    showJybCoinFeatures,
    userKnapsackHctCaiFee: prepare.userKnapsackHctCaiFee,
    jyHctCai,
  })
  const jyHctCaiFee = computeJyHctCaiFee({
    srLabel: getters.shippingRatesInfo.srLabel,
    finalUseFirstCarrier,
    jyHctCai,
    useKnsCount: jyHctCaiFeeData.useKnsCount,
    oneCaiFee: computeOneCaiFee({ prepareModel: prepare }),
  })
  const preHctPalletFee = resolvePreHctPalletFee({ state, getters, rootState, policy })
  return {
    jyHctCaiFee,
    hctPalletFee: computeHctPalletFee({
      srLabel: getters.shippingRatesInfo.srLabel,
      finalUseFirstCarrier,
      preHctPalletFee,
    }),
  }
}

export function resolveBusyFeeData ({ state, getters, rootState, rootGetters, feeInfo }) {
  const prepare = state.createPrepare
  const isBusying = computeIsBusying({
    addrid: getters.shippingRatesInfo.addrid,
    transport: getters.shippingRatesInfo.transport,
    isBusySeason: rootGetters['fee/busySeason/isBusySeason'],
    userJypointBalance: prepare.userJypointBalance,
  })
  const jyoBusyFeeWg = computeJyoBusyFeeWg({
    parcelsFeeFormulas: getters.shippingRatesInfo.parcelsFeeFormulas,
  })
  return computeJyoBusyFeeData({
    finalSr: feeInfo.finalSr,
    shippingRatesInfo: getters.shippingRatesInfo,
    isBusying,
    jyoBusyFeeWg,
    isLong91: rootGetters.isLong91,
    showJybCoinFeatures: canShowJybCoinFeatures(rootState.project),
    hasJyorder: rootState.userData.hasJyorder,
    userKnapsackBusyfee128: prepare.userKnapsackBusyfee128,
    userKnapsackBusyfee158: prepare.userKnapsackBusyfee158,
  })
}

export function buildFeeSummaryFacade ({ state, getters, rootState, rootGetters = {} }) {
  if (!state.createForm || !state.createPrepare) return null
  const policy = platformPolicy(rootState.project)
  const form = state.createForm
  const prepare = state.createPrepare
  const shippingRatesInfo = getters.shippingRatesInfo
  const finalUseFirstCarrier = getters.finalUseFirstCarrier || resolveFinalUseFirstCarrier({ state, getters, policy })
  const isBizFee = resolveIsBizFee({ state, getters, rootState })
  const formulaContext = {
    sr: shippingRatesInfo,
    isBizFee,
    useFeeType: form.useFeeType,
    jyoShpMethod: form.jyoShpMethod,
    isAllIncTax: getters.isAllIncTax,
    isCompanyMBPackedTax: getters.isCompanyMBPackedTax,
    isCompanyAddr: getters.isCompanyAddr,
    finalUseFirstCarrier,
  }
  const parcelsFeeFormulas = computeParcelsFeeFormulas(formulaContext)
  const feeInfoContext = {
    allItemInclFreight: prepare.parcelsInfo.allItemInclFreight,
    shippingRatesInfo,
    seaInfo: getters.seaInfo,
    parcelsFeeFormulas,
    isBizFee,
    jyoShpMethod: form.jyoShpMethod,
    useFeeType: form.useFeeType,
    useFinalDiscountRate: state.createView.useFinalDiscountRate,
    bestDiscountSr: getters.bestDiscountSr,
    edition: rootState.edition,
    addrInfo: state.currAddrInfo,
  }
  const feeInfo = computeFeeInfo(feeInfoContext)
  const finalSrParcelsFeeFormulas = computeFinalSrParcelsFeeFormulas({
    ...formulaContext,
    sr: feeInfo.finalSr,
  })
  const sedeFee = getters.sedeFeeOf(feeInfo.finalSr)
  const hctFees = resolveHctFees({
    state,
    getters,
    rootState,
    finalUseFirstCarrier,
    policy,
  })
  const jyoBusyFee = (getters.busyFeeData || resolveBusyFeeData({
    state,
    getters,
    rootState,
    rootGetters,
    feeInfo,
  })).fee
  const localFee = computeLocalFee({
    addrInfo: state.currAddrInfo,
    sedeFee,
    jyoCarrier: form.jyoCarrier || 'hct',
    parcelsFeeFormulas,
    srLabel: shippingRatesInfo.srLabel,
  })
  const base = buildFeeBaseSnapshot({
    feeInfoContext,
    additionalFeeContext: {
      form,
      whsInfoFee: prepare.whsInfoFee,
      shippingAddls: prepare.shippingAddls,
      addrInfo: state.currAddrInfo,
    },
    shippingRatesInfo,
    seaParcelsFeeInfo: getters.seaParcelsFeeInfo,
    finalSrParcelsFeeFormulas,
    jyoBusyFee,
    jyoOvldFee: resolveJyoOvldFee({ state, getters }),
    ...hctFees,
    otherFees: getters.otherFees,
    sedeFee,
    localFee,
    vanFee: form.jyoCarrier === 'van' ? getters.vanFee : 0,
    isAllIncTax: getters.isAllIncTax,
    cneeAddressDiscount: getters.cneeAddressDiscount,
    hasShippingRate: Boolean(shippingRatesInfo.transport),
    prepareModel: prepare,
    tradevanAddr: state.currTradevanAddr,
    estimateWeight: state.createView.estimateWeight,
    currency: form.currency,
  }, policy)
  const summary = buildFinalFeeSummary(base, {
    currCoupon: getters.currCoupon,
    usableList: getters.couponUsableList,
  }, {
    couponBeforeTimeAndTax: policy.couponBeforeTimeAndTax,
    legacyAmount: null,
  })
  const damageInsurancePremium = isOrderCreateDamageInsuranceEnabled({
    project: rootState.project,
    parcels: prepare.parcels,
  })
    ? totalSelectedDamageInsurancePremium(
      prepare.parcels,
      state.createView?.damageInsuranceSelections,
      shippingRatesInfo.transport
    )
    : 0
  return {
    ...summary,
    feeBreakdown: buildFeeRows(summary, { damageInsurancePremium }),
  }
}

export const getters = {
  factualFirstCarrier (state, getters, rootState) {
    return resolveFactualFirstCarrier({
      state,
      getters,
      policy: platformPolicy(rootState.project),
    })
  },
  finalUseFirstCarrier (state, getters, rootState) {
    return resolveFinalUseFirstCarrier({
      state,
      getters,
      policy: platformPolicy(rootState.project),
    })
  },
  preHctPalletFee (state, getters, rootState) {
    return resolvePreHctPalletFee({
      state,
      getters,
      rootState,
      policy: platformPolicy(rootState.project),
    })
  },
  busyFeeData (state, getters, rootState, rootGetters) {
    if (!state.createForm || !state.createPrepare) return null
    const finalUseFirstCarrier = getters.finalUseFirstCarrier || resolveFinalUseFirstCarrier({
      state,
      getters,
      policy: platformPolicy(rootState.project),
    })
    const isBizFee = resolveIsBizFee({ state, getters, rootState })
    const parcelsFeeFormulas = computeParcelsFeeFormulas({
      sr: getters.shippingRatesInfo,
      isBizFee,
      useFeeType: state.createForm.useFeeType,
      jyoShpMethod: state.createForm.jyoShpMethod,
      isAllIncTax: getters.isAllIncTax,
      isCompanyMBPackedTax: getters.isCompanyMBPackedTax,
      isCompanyAddr: getters.isCompanyAddr,
      finalUseFirstCarrier,
    })
    const feeInfo = computeFeeInfo({
      allItemInclFreight: state.createPrepare.parcelsInfo.allItemInclFreight,
      shippingRatesInfo: getters.shippingRatesInfo,
      seaInfo: getters.seaInfo,
      parcelsFeeFormulas,
      isBizFee,
      jyoShpMethod: state.createForm.jyoShpMethod,
      useFeeType: state.createForm.useFeeType,
      useFinalDiscountRate: state.createView.useFinalDiscountRate,
      bestDiscountSr: getters.bestDiscountSr,
      edition: rootState.edition,
      addrInfo: state.currAddrInfo,
    })
    return resolveBusyFeeData({ state, getters, rootState, rootGetters, feeInfo })
  },
  feeSummary (state, getters, rootState, rootGetters) {
    return buildFeeSummaryFacade({ state, getters, rootState, rootGetters })
  },
  parcelsFeeFormulas (state, getters) {
    return getters.feeSummary?.base?.feeInfo?.parcelsFeeFormulas || {}
  },
}

export { platformPolicy }
